import { InvalidContentError, isContentFragment } from '@oneaudi/falcon-tools';

import type { ContentServiceV1 } from '@oneaudi/content-service';
import type {
  AudiFooterResponse,
  Category,
  ContentHeadless,
  headlessCategoryType,
  headlessLanguageLinkType,
  headlessLinkType,
  headlessSocialMediaLinkType,
  LanguageLink,
  Link,
  SocialMediaType,
} from '../types/audi-footer-response.types';

function isFalconContent(content: unknown): content is ContentHeadless {
  return isContentFragment(content);
}

export function normalizeHeadlessData(contentService: ContentServiceV1): AudiFooterResponse {
  const content = contentService.getContent() as ContentHeadless;
  if (!content) {
    console.warn('No Editor content found for Footer');

    return {} as AudiFooterResponse;
  }
  if (!isFalconContent(content)) {
    throw new InvalidContentError('Content is invalid.');
  }
  const categories: Category[] = [];
  content.fields.Categories.forEach((category: headlessCategoryType) => {
    const subLinks: Link[] = [];
    category.fields.SubLinks.forEach((link: headlessLinkType) => {
      subLinks.push({
        IsOverview: false,
        Type: link.fields.Type,
        AriaLabel: link.fields['Aria Label'],
        Target: link.fields.Target,
        Text: link.fields.Text,
        Url: link.fields.Url,
      });
    });
    categories.push({
      Link: {
        Text: category.fields.CategoryTitle
          ? category.fields.CategoryTitle
          : category.fields.Link.fields.Text,
        Url: '',
        Target: '',
        Type: '',
      },
      SubLinks: subLinks,
    });
  });
  const legalLinks: Link[] = [];
  content.fields.LegalLinks.forEach((legalLink: headlessLinkType) => {
    legalLinks.push({
      IsOverview: false,
      Type: legalLink.fields.Type,
      AriaLabel: legalLink.fields['Aria Label'],
      Target: legalLink.fields.Target,
      Text: legalLink.fields.Text,
      Url: legalLink.fields.Url,
    });
  });
  const languageLinks: LanguageLink[] = [];
  content.fields.LanguageLinks.forEach((languageLink: headlessLanguageLinkType, index: number) => {
    languageLinks.push({
      Text: languageLink.fields.Text,
      Url: languageLink.fields.Url,
      IsSelected: index === 0,
    });
  });
  const socialMedia: SocialMediaType[] = [];
  content.fields.SocialMedia?.forEach((socialMediaItem: headlessSocialMediaLinkType) => {
    socialMedia.push({
      Id: socialMediaItem.fields.Id,
      Link: {
        Target: socialMediaItem.fields.Target,
        Text: socialMediaItem.fields.Text,
        Url: socialMediaItem.fields.Url,
      },
    });
  });
  return {
    Categories: categories,
    Copyright: content.fields.Copyright,
    ToTopLabel: content.fields.ToTopLabel,
    LanguageLinks: languageLinks,
    LegalLinks: legalLinks,
    SocialMedia: socialMedia,
    Disclaimer: content.fields.Disclaimer,
    ImageUrl: content.fields.CustomFooterImage?.path,
    ImageText: content.fields.CustomFooterImageText,
    FooterImageAriaLabel: content.fields.LegalCopy,
    enableMiniFooter: content.fields.enableMiniFooter,
  };
}
